// Payment Service
// This service handles all payment-related API calls and business logic
// Separated from UI components for better maintainability and testing

import { apiService, type ApiResponse } from '@/services/apiService';
import { resolveGameWalletErrorMessage } from '@/utils/gameWalletErrorMessages';

function resolveForbiddenPaymentError(error: any, fallbackMessage?: string) {
    return {
        success: false,
        message: resolveGameWalletErrorMessage(error, undefined, error.message || fallbackMessage),
        error: error.code || 'FORBIDDEN',
        statusCode: 403,
    };
}

export interface DepositRequest {
    provider?: string;
    amount: number;
    paymentMethod: string;
    meldServiceProvider?: string;
    description?: string;
}

export interface MeldServiceProvider {
    serviceProvider: string;
    name: string;
    status?: string | null;
    categories?: string[];
    logos?: {
        dark?: string;
        darkShort?: string;
        light?: string;
        lightShort?: string;
    } | null;
    websiteUrl?: string | null;
    customerSupportUrl?: string | null;
}

export interface WithdrawalRequest {
    amount: number;
    withdrawalMethod: 'bank_transfer' | 'paypal' | 'check';
}

export interface Transaction {
    id: string;
    type: 'deposit' | 'withdrawal';
    amount: number;
    status: 'pending' | 'completed' | 'failed' | 'cancelled';
    method: string;
    createdAt: string;
    completedAt?: string;
    estimatedCompletion?: string;
}

export interface Balance {
    balance: number;
    currency: string;
    lastUpdated: string;
}

export interface GameWallet {
    id: string;
    provider: {
        id: number;
        code: string;
        name: string;
        currency: string;
    };
    walletBalance: number;
    inGameBalance: number;
    /** River Pay sweepstakes: withdrawable (from provider `winning`); other games 0 */
    winningBalance?: number;
    syncStatus: string;
    lastSyncedAt: string;
    lastSyncSource: string;
    metadata: Record<string, any>;
    updatedAt: string;
    createdAt: string;
}

export interface BalanceWithGameWallets {
    balance: number;
    totalBalance?: number;
    reservedBalance?: number;
    currency: string;
    lastUpdated: string;
    gameWallets: GameWallet[];
    gameWalletTotals: {
        walletBalance: number;
        inGameBalance: number;
        pendingCount: number;
        failedCount: number;
        count: number;
    };
}

/** Single item in reserved balance detail (e.g. a pending withdrawal). */
export interface ReservedBalanceDetailItem {
    transactionId: string;
    amount: number;
    currency: string;
    status: string;
    type: string;
    paymentMethod: string;
    createdAt: string;
    hasReservation: boolean;
    linkedPaymentTransactionId?: string;
}

/** Response of GET /api/payment/reserved-balance-detail */
export interface ReservedBalanceDetailResponse {
    reservedBalance: number;
    totalBalance: number;
    availableBalance: number;
    currency: string;
    items: ReservedBalanceDetailItem[];
}

export interface PaymentResponse {
    success: boolean;
    message: string;
    data?: any;
    error?: string;
    statusCode?: number;
    insufficientWallet?: {
        availableBalance: number;
        requiredAmount: number;
    };
}

export interface TransactionHistoryResponse {
    success: boolean;
    message: string;
    data?: {
        transactions: Transaction[];
        pagination: {
            page: number;
            limit: number;
            total: number;
            totalPages: number;
        };
    };
    error?: string;
}

export interface GameWithdrawRequest {
    gameProviderId: string;
    amount: number;
    description?: string;
}

export interface GameWithdrawResponse {
    success: boolean;
    message: string;
    error?: string;
    data?: {
        amount: number;
        transactionId: string;
        /**
         * `'pending'`: the move is only requested — staff approve it first (manual-withdrawal games,
         * amounts at or over the auto-approve threshold, any other move held for review) and nothing
         * is in the wallet yet. A move that landed answers without a status.
         */
        status?: string;
        gameProvider: {
            id: string;
            code: string;
            name: string;
            currency: string;
        };
        gameWallet: {
            id: string;
            walletBalance: number;
            inGameBalance: number;
            winningBalance?: number;
            syncStatus: string;
        };
        adapterResult: {
            inGameBalance: number;
            winningBalance?: number;
            account: string;
        };
        message: string;
    };
    statusCode?: number;
}

export interface GameCashoutRequest {
    gameProviderId: string;
    amount: number;
    description?: string;
    withdrawalType: 'bank_transfer' | 'bitcoin_transfer';
    addressId?: string;
    saveAddress?: boolean;
    // For bank_transfer:
    bankCode?: string;
    accountNumber?: string;
    accountName?: string;
    routingNumber?: string;
    accountType?: 'checking' | 'savings';
    // For bitcoin_transfer:
    bitcoinAddress?: string;
    bitcoinNetwork?: string;
    bitcoinMemo?: string;
}

export interface GameCashoutResponse {
    success: boolean;
    message: string;
    data?: {
        amount: number;
        gameWithdrawalTransactionId: string;
        withdrawalRequestTransactionId: string;
        gameProvider: {
            id: string;
            code: string;
            name: string;
            currency: string;
        };
        gameWallet: {
            id: string;
            walletBalance: number;
            inGameBalance: number;
            winningBalance?: number;
            syncStatus: string;
        };
        withdrawalRequest: {
            transactionId: string;
            withdrawalType: 'bank_transfer' | 'bitcoin_transfer';
            amount: number;
            currency: string;
            status: string;
            bankInfo?: {
                bankCode?: string;
                accountNumber?: string;
                accountName?: string;
                routingNumber?: string;
                accountType?: string;
            };
            bitcoinInfo?: {
                bitcoinAddress?: string;
                bitcoinNetwork?: string;
                bitcoinMemo?: string;
            };
        };
        adapterResult?: {
            inGameBalance?: number;
            winningBalance?: number;
            account?: string;
        };
    };
    statusCode?: number;
}

export interface MultiDepositRequest {
    provider?: string; // e.g. 'btcpay' for BTCPay deposit-multi
    amount: number;
    paymentMethod: string; // e.g. 'paypal', 'btc_onchain', 'ltc_chain', 'doge_chain', 'usdt'
    meldServiceProvider?: string;
    description?: string;
    allocations: Array<{
        gameProviderId: string;
        amount: number;
    }>;
}

export interface MultiDepositResponse {
    success: boolean;
    message: string;
    data?: {
        transactionId?: string;
        amount: number;
        status: string;
        allocations?: Array<{
            gameProviderId: string;
            amount: number;
            status?: string;
            depositType?: 'auto' | 'manual';
        }>;
        createdTransactions?: Array<{
            gameProviderId: string;
            transactionId?: string | number;
            success: boolean;
            manualDeposit?: boolean;
            status?: string;
            message?: string;
            error?: string;
        }>;
        // Payment URL for redirecting to payment gateway
        paymentUrl?: string;
        // QR code data (base64 image)
        qrCode?: string;
        // Crypto address for payment
        address?: string;
        // Expiration time
        expiresAt?: string;
        // Order number
        orderNumber?: string;
        // Currency
        currency?: string;
    };
    error?: string;
    statusCode?: number;
    insufficientWallet?: {
        availableBalance: number;
        requiredAmount: number;
    };
}

export interface MultiDepositStatusResponse {
    success: boolean;
    message: string;
    data?: {
        transactionId: string;
        parentTransactionDbId: number;
        status: string;
        syncStatus: string;
        allocations: Array<{
            transactionId: string;
            paymentTransactionId: number;
            gameProviderId: string;
            amount: number;
            status: string;
            syncStatus: string;
            updatedAt: string;
        }>;
    };
    error?: string;
    statusCode?: number;
}

class PaymentService {
    async getMeldServiceProviders(): Promise<MeldServiceProvider[]> {
        const response = await apiService.get<{ providers?: MeldServiceProvider[] }>(
            '/payment/meld/service-providers',
            true,
            true
        );
        if (!response.success) {
            throw new Error(response.message || 'Failed to load Meld providers');
        }
        return response.data?.providers || [];
    }

    /**
     * Pre-check shop CDN credit before online deposit (shop-linked players).
     */
    async precheckDeposit(
        amount: number,
        provider?: string,
        paymentMethod?: string,
        depositFlow: 'wallet' | 'game' = 'game'
    ): Promise<PaymentResponse> {
        try {
            const params = new URLSearchParams({ amount: String(amount), depositFlow });
            if (provider) params.set('provider', provider);
            if (paymentMethod) params.set('paymentMethod', paymentMethod);
            const response = await apiService.get(
                `/payment/deposit-precheck?${params.toString()}`,
                true,
                true
            );
            return {
                success: response.success,
                message: response.message
            };
        } catch (error: any) {
            return {
                success: false,
                message: error.message || 'Deposit precheck failed',
                error: 'PRECHECK_FAILED'
            };
        }
    }

    /**
     * Processes a deposit request
     */
    async processDeposit(depositData: DepositRequest): Promise<PaymentResponse> {
        try {
            // Validate deposit data
            const validation = this.validateDepositData(depositData);
            if (!validation.isValid) {
                return {
                    success: false,
                    message: validation.message,
                    error: 'VALIDATION_ERROR'
                };
            }

            const precheck = await this.precheckDeposit(
                depositData.amount,
                depositData.provider,
                depositData.paymentMethod,
                'wallet'
            );
            if (!precheck.success) {
                return {
                    success: false,
                    message: precheck.message,
                    error: precheck.error || 'PRECHECK_FAILED'
                };
            }

            // Make API call to backend
            const response = await apiService.post('/payment/deposit', depositData, true);

            // Clear user cache after successful deposit (balance will change)
            if (response.success) {
                apiService.clearUserCache();
            }

            return {
                success: response.success,
                message: response.message,
                data: response.data
            };

        } catch (error: any) {
            console.error('Error processing deposit:', error);

            // Handle specific error cases
            if (error.statusCode === 401) {
                return {
                    success: false,
                    message: 'Authentication required. Please log in again.',
                    error: 'UNAUTHORIZED'
                };
            }

            if (error.statusCode === 403) {
                return resolveForbiddenPaymentError(error, 'Insufficient permissions for deposits');
            }

            if (error.statusCode === 422) {
                return {
                    success: false,
                    message: error.message || 'Invalid deposit data',
                    error: 'VALIDATION_ERROR'
                };
            }

            return {
                success: false,
                message: error.message || 'Deposit processing failed. Please try again.',
                error: 'NETWORK_ERROR'
            };
        }
    }

    /**
     * Processes a withdrawal request
     */
    async processWithdrawal(withdrawalData: WithdrawalRequest): Promise<PaymentResponse> {
        try {
            // Validate withdrawal data
            const validation = this.validateWithdrawalData(withdrawalData);
            if (!validation.isValid) {
                return {
                    success: false,
                    message: validation.message,
                    error: 'VALIDATION_ERROR'
                };
            }

            // Make API call to backend
            const response = await apiService.post('/payment/withdrawal', withdrawalData, true);

            const data = response.data as { status?: string; errorMessage?: string } | undefined;
            const withdrawalFailed = data?.status === 'failed';

            // Clear user cache only when withdrawal actually succeeded (not failed by provider)
            if (response.success && !withdrawalFailed) {
                apiService.clearUserCache();
            }

            return {
                success: response.success && !withdrawalFailed,
                message: withdrawalFailed && data?.errorMessage ? data.errorMessage : response.message,
                data: response.data
            };

        } catch (error: any) {
            console.error('Error processing withdrawal:', error);

            // Handle specific error cases
            if (error.statusCode === 401) {
                return {
                    success: false,
                    message: 'Authentication required. Please log in again.',
                    error: 'UNAUTHORIZED'
                };
            }

            if (error.statusCode === 403) {
                return resolveForbiddenPaymentError(error, 'Insufficient permissions for withdrawals');
            }

            if (error.statusCode === 409) {
                return {
                    success: false,
                    message: resolveGameWalletErrorMessage(error, undefined, error.message),
                    error: error.code || 'CONFLICT',
                    statusCode: 409,
                };
            }

            if (error.statusCode === 422) {
                return {
                    success: false,
                    message: error.message || 'Invalid withdrawal data or insufficient funds',
                    error: 'VALIDATION_ERROR'
                };
            }

            return {
                success: false,
                message: error.message || 'Withdrawal processing failed. Please try again.',
                error: 'NETWORK_ERROR'
            };
        }
    }

    /**
     * Gets user's current balance
     */
    async getBalance(): Promise<PaymentResponse> {
        try {
            const response = await apiService.get('/wallet/balance', true);

            return {
                success: response.success,
                message: response.message,
                data: response.data
            };

        } catch (error: any) {
            console.error('Error getting balance:', error);

            if (error.statusCode === 401) {
                return {
                    success: false,
                    message: 'Authentication required. Please log in again.',
                    error: 'UNAUTHORIZED'
                };
            }

            return {
                success: false,
                message: error.message || 'Failed to retrieve balance',
                error: 'NETWORK_ERROR'
            };
        }
    }

    /**
     * Gets user's balance with game wallets
     * This includes main balance and all game provider balances
     * @param forceRefresh - If true, bypasses cache and fetches fresh data
     */
    async getBalanceWithGameWallets(forceRefresh: boolean = false): Promise<ApiResponse<BalanceWithGameWallets>> {
        try {
            const response = await apiService.get<BalanceWithGameWallets>('/payment/balance', true, forceRefresh);

            return response;

        } catch (error: unknown) {
            console.error('Error getting balance with game wallets:', error);

            if (error && typeof error === 'object' && 'statusCode' in error) {
                const apiError = error as { statusCode: number; message?: string };
                if (apiError.statusCode === 401) {
                    const err = new Error('Authentication required. Please log in again.');
                    (err as any).statusCode = 401;
                    (err as any).error = 'UNAUTHORIZED';
                    throw err;
                }
                const err = new Error(apiError.message || 'Failed to retrieve balance');
                (err as any).statusCode = apiError.statusCode || 500;
                (err as any).error = 'NETWORK_ERROR';
                throw err;
            }

            const err = new Error('Failed to retrieve balance');
            (err as any).statusCode = 500;
            (err as any).error = 'NETWORK_ERROR';
            throw err;
        }
    }

    /**
     * Gets reserved balance detail (breakdown of what is reserved, e.g. pending withdrawals).
     */
    async getReservedBalanceDetail(): Promise<ApiResponse<ReservedBalanceDetailResponse>> {
        try {
            const response = await apiService.get<ReservedBalanceDetailResponse>('/payment/reserved-balance-detail', true);
            return response;
        } catch (error: unknown) {
            console.error('Error getting reserved balance detail:', error);
            if (error && typeof error === 'object' && 'statusCode' in error) {
                const apiError = error as { statusCode: number; message?: string };
                const err = new Error(apiError.message || 'Failed to retrieve reserved balance detail');
                (err as any).statusCode = apiError.statusCode || 500;
                throw err;
            }
            throw new Error('Failed to retrieve reserved balance detail');
        }
    }

    /**
     * Gets transaction history
     */
    async getTransactionHistory(page: number = 1, limit: number = 20, type?: 'deposit' | 'withdrawal'): Promise<TransactionHistoryResponse> {
        try {
            const params = new URLSearchParams({
                page: page.toString(),
                limit: limit.toString()
            });

            if (type) {
                params.append('type', type);
            }

            const response = await apiService.get<TransactionHistoryResponse['data']>(
                `/payment/history?${params.toString()}`,
                true
            );

            return {
                success: response.success,
                message: response.message,
                data: response.data
            };

        } catch (error: any) {
            console.error('Error getting transaction history:', error);

            if (error.statusCode === 401) {
                return {
                    success: false,
                    message: 'Authentication required. Please log in again.',
                    error: 'UNAUTHORIZED'
                };
            }

            return {
                success: false,
                message: error.message || 'Failed to retrieve transaction history',
                error: 'NETWORK_ERROR'
            };
        }
    }

    /**
     * Withdraws money from game wallet to main wallet
     * This calls the /api/game-wallet/withdraw endpoint (Flow 1)
     */
    async withdrawFromGame(withdrawData: GameWithdrawRequest): Promise<GameWithdrawResponse> {
        try {
            // Validate withdrawal data
            if (!withdrawData.gameProviderId) {
                return {
                    success: false,
                    message: 'Game provider ID is required',
                    statusCode: 400
                };
            }

            if (!withdrawData.amount || withdrawData.amount <= 0) {
                return {
                    success: false,
                    message: 'Amount must be greater than 0',
                    statusCode: 400
                };
            }

            // Make API call to backend – POST /api/game-wallet/withdraw
            const endpoint = '/game-wallet/withdraw';
            if (import.meta.env.DEV && typeof console !== 'undefined' && console.log) {
                console.log('[PaymentService] Calling withdrawFromGame API:', endpoint, withdrawData);
            }
            const response = await apiService.post<GameWithdrawResponse['data']>(endpoint, withdrawData, true);

            // Clear user cache after successful game withdrawal (balance will change)
            if (response.success) {
                apiService.clearUserCache();
            }

            return {
                success: response.success,
                message: response.message,
                data: response.data,
                statusCode: response.success ? 200 : 400
            };

        } catch (error: any) {
            console.error('Error withdrawing from game:', error);

            // Handle specific error cases
            if (error.statusCode === 401) {
                return {
                    success: false,
                    message: 'Authentication required. Please log in again.',
                    statusCode: 401
                };
            }

            if (error.statusCode === 403) {
                return resolveForbiddenPaymentError(error, 'Insufficient permissions for game withdrawals');
            }

            if (error.statusCode === 404) {
                return {
                    success: false,
                    message: error.message || 'Game provider not found',
                    statusCode: 404
                };
            }

            if (error.statusCode === 400) {
                return {
                    success: false,
                    message: error.message || 'Invalid withdrawal data or insufficient balance in game',
                    statusCode: 400
                };
            }

            if (error.statusCode === 409) {
                return {
                    success: false,
                    message: resolveGameWalletErrorMessage(error, undefined, error.message, 'withdraw'),
                    error: error.code || 'CONFLICT',
                    statusCode: 409,
                };
            }

            if (error.statusCode === 503) {
                return {
                    success: false,
                    message: resolveGameWalletErrorMessage(error, undefined, error.message, 'withdraw'),
                    error: error.code || 'SERVICE_UNAVAILABLE',
                    statusCode: 503,
                };
            }

            return {
                success: false,
                message: error.message || 'Failed to withdraw from game. Please try again.',
                statusCode: error.statusCode || 500
            };
        }
    }

    /**
     * Cashout from game with withdrawal request
     * This calls the /api/game-wallet/cashout endpoint (Flow 2)
     * Flow: Pick game → Enter bank account/bitcoin wallet → Withdraw from game → Into user wallet → Create withdrawal request
     */
    async cashoutFromGame(cashoutData: GameCashoutRequest): Promise<GameCashoutResponse> {
        console.log('[PaymentService] cashoutFromGame called with data:', {
            gameProviderId: cashoutData.gameProviderId,
            amount: cashoutData.amount,
            withdrawalType: cashoutData.withdrawalType,
            hasBankInfo: !!(cashoutData.bankCode && cashoutData.accountNumber),
            hasBitcoinInfo: !!(cashoutData.bitcoinAddress && cashoutData.bitcoinNetwork)
        });

        try {
            // Validate cashout data
            if (!cashoutData.gameProviderId) {
                return {
                    success: false,
                    message: 'Game provider ID is required',
                    statusCode: 400
                };
            }

            if (!cashoutData.amount || cashoutData.amount <= 0) {
                return {
                    success: false,
                    message: 'Amount must be greater than 0',
                    statusCode: 400
                };
            }

            if (!cashoutData.withdrawalType || !['bank_transfer', 'bitcoin_transfer'].includes(cashoutData.withdrawalType)) {
                return {
                    success: false,
                    message: 'Withdrawal type must be either bank_transfer or bitcoin_transfer',
                    statusCode: 400
                };
            }

            // Validate bank transfer fields
            if (cashoutData.withdrawalType === 'bank_transfer') {
                if (!cashoutData.bankCode || !cashoutData.accountNumber || !cashoutData.accountName || !cashoutData.routingNumber) {
                    return {
                        success: false,
                        message: 'Bank transfer requires bankCode, accountNumber, accountName, and routingNumber',
                        statusCode: 400
                    };
                }
            }

            // Validate bitcoin transfer fields
            if (cashoutData.withdrawalType === 'bitcoin_transfer') {
                if (!cashoutData.bitcoinAddress || !cashoutData.bitcoinNetwork) {
                    return {
                        success: false,
                        message: 'Bitcoin transfer requires bitcoinAddress and bitcoinNetwork',
                        statusCode: 400
                    };
                }
            }

            // Make API call to backend
            const response = await apiService.post<GameCashoutResponse['data']>('/game-wallet/cashout', cashoutData, true);

            // Clear user cache after successful cashout (balance will change)
            if (response.success) {
                apiService.clearUserCache();
            }

            return {
                success: response.success,
                message: response.message,
                data: response.data,
                statusCode: response.success ? 200 : 400
            };

        } catch (error: any) {
            console.error('Error cashout from game:', error);

            // Handle specific error cases
            if (error.statusCode === 401) {
                return {
                    success: false,
                    message: 'Authentication required. Please log in again.',
                    statusCode: 401
                };
            }

            if (error.statusCode === 403) {
                return resolveForbiddenPaymentError(error, 'Insufficient permissions for game cashout');
            }

            if (error.statusCode === 404) {
                return {
                    success: false,
                    message: error.message || 'Game provider not found',
                    statusCode: 404
                };
            }

            if (error.statusCode === 400) {
                return {
                    success: false,
                    message: error.message || 'Invalid cashout data or insufficient balance in game',
                    statusCode: 400
                };
            }

            return {
                success: false,
                message: error.message || 'Failed to cashout from game. Please try again.',
                statusCode: error.statusCode || 500
            };
        }
    }

    /**
     * Processes a multi-game deposit request
     * @param depositData - Multi-deposit request data with allocations
     * @returns Promise with deposit result
     */
    async processMultiDeposit(
        depositData: MultiDepositRequest,
        options?: { skipShopPrecheck?: boolean }
    ): Promise<MultiDepositResponse> {
        try {
            // Validate deposit data
            if (!depositData.amount || depositData.amount <= 0) {
                return {
                    success: false,
                    message: 'Amount must be greater than 0',
                    error: 'VALIDATION_ERROR',
                    statusCode: 400
                };
            }

            // Min per-allocation do backend enforce theo config admin (game_providers.min_deposit,
            // multiGameDepositService.js:341). Do NOT hardcode a total min on the FE anymore.

            if (!depositData.allocations || depositData.allocations.length === 0) {
                return {
                    success: false,
                    message: 'At least one game allocation is required',
                    error: 'VALIDATION_ERROR',
                    statusCode: 400
                };
            }

            // Validate allocations sum matches total amount
            const allocationsSum = depositData.allocations.reduce((sum, alloc) => sum + alloc.amount, 0);
            if (Math.abs(allocationsSum - depositData.amount) > 0.01) {
                return {
                    success: false,
                    message: 'Allocations sum must match total amount',
                    error: 'VALIDATION_ERROR',
                    statusCode: 400
                };
            }

            // Validate each allocation
            for (const allocation of depositData.allocations) {
                if (!allocation.gameProviderId) {
                    return {
                        success: false,
                        message: 'All allocations must have a valid game provider ID',
                        error: 'VALIDATION_ERROR',
                        statusCode: 400
                    };
                }
                if (!allocation.amount || allocation.amount <= 0) {
                    return {
                        success: false,
                        message: 'All allocation amounts must be greater than 0',
                        error: 'VALIDATION_ERROR',
                        statusCode: 400
                    };
                }
            }

            if (!options?.skipShopPrecheck) {
                const precheck = await this.precheckDeposit(
                    depositData.amount,
                    depositData.provider,
                    depositData.paymentMethod
                );
                if (!precheck.success) {
                    return {
                        success: false,
                        message: precheck.message,
                        error: precheck.error || 'PRECHECK_FAILED',
                        statusCode: 400
                    };
                }
            }

            // Reload token from localStorage to ensure we have the latest token
            // This handles cases where token was set after login but apiService instance wasn't updated
            apiService.reloadTokens();

            // Log token status for debugging (remove in production if needed)
            const token = apiService.getAccessToken();
            console.log('[PaymentService] processMultiDeposit - Token status:', {
                hasToken: !!token,
                tokenLength: token?.length || 0,
                tokenPreview: token ? `${token.substring(0, 20)}...` : 'null'
            });

            // Make API call to backend
            // Let API handle authentication - it will return 401 if token is invalid
            const response = await apiService.post<MultiDepositResponse['data']>('/payment/deposit-multi', depositData, true);

            // Clear user cache after successful deposit (balance will change)
            if (response.success) {
                apiService.clearUserCache();
            }

            return {
                success: response.success,
                message: response.message,
                data: response.data,
                error: response.error,
                statusCode: response.statusCode
            };

        } catch (error: any) {
            console.error('Error processing multi-deposit:', error);

            // Handle specific error cases
            if (error.statusCode === 401) {
                return {
                    success: false,
                    message: 'Authentication required. Please log in again.',
                    error: 'UNAUTHORIZED',
                    statusCode: 401
                };
            }

            if (error.statusCode === 403) {
                return resolveForbiddenPaymentError(error, 'Insufficient permissions for deposits');
            }

            if (error.statusCode === 422) {
                return {
                    success: false,
                    message: error.message || 'Invalid deposit data',
                    error: 'VALIDATION_ERROR',
                    statusCode: 422
                };
            }

            if (error.statusCode === 409) {
                return {
                    success: false,
                    message: resolveGameWalletErrorMessage(error, undefined, error.message, 'deposit'),
                    error: error.code || 'CONFLICT',
                    statusCode: 409,
                };
            }

            if (
                error.statusCode === 400 &&
                (error.code === 'INSUFFICIENT_WALLET_BALANCE' || error.error === 'INSUFFICIENT_WALLET_BALANCE')
            ) {
                const payload = (error.data ?? error.details ?? error) as {
                    availableBalance?: number;
                    requiredAmount?: number;
                };
                return {
                    success: false,
                    message: error.message || 'Insufficient wallet balance. Please top up your wallet first.',
                    error: 'INSUFFICIENT_WALLET_BALANCE',
                    statusCode: 400,
                    insufficientWallet: {
                        availableBalance: Number(payload.availableBalance ?? 0),
                        requiredAmount: Number(payload.requiredAmount ?? 0),
                    },
                };
            }

            if (error.statusCode === 503) {
                return {
                    success: false,
                    message: resolveGameWalletErrorMessage(error, undefined, error.message, 'deposit'),
                    error: error.code || 'SERVICE_UNAVAILABLE',
                    statusCode: 503,
                };
            }

            return {
                success: false,
                message: error.message || 'Deposit processing failed. Please try again.',
                error: 'NETWORK_ERROR',
                statusCode: error.statusCode || 500
            };
        }
    }

    async getMultiDepositStatus(transactionId: string): Promise<MultiDepositStatusResponse> {
        try {
            const response = await apiService.get<MultiDepositStatusResponse['data']>(
                `/payment/deposit-multi-status/${transactionId}`,
                true
            );
            return {
                success: response.success,
                message: response.message,
                data: response.data,
                error: response.error,
                statusCode: response.statusCode
            };
        } catch (error: any) {
            return {
                success: false,
                message: error.message || 'Failed to get multi-game deposit status',
                error: 'NETWORK_ERROR',
                statusCode: error.statusCode || 500
            };
        }
    }

    /**
     * Syncs game wallet balance from game provider
     * @param gameWalletId - The UUID of the game wallet to sync
     * @returns Promise with sync result
     */
    async syncGameWallet(gameWalletId: string): Promise<ApiResponse<{
        gameWallet?: GameWallet;
        syncStatus?: string;
        lastSyncedAt?: string;
        source?: string;
        failedAt?: string;
        lastError?: string;
        errorSource?: string;
        transferredAt?: string;
    }>> {
        try {
            if (!gameWalletId) {
                return {
                    success: false,
                    message: 'Game wallet ID is required',
                    statusCode: 400
                };
            }

            // Call API to sync game wallet
            // Note: This endpoint might be /admin/game-wallets/{id}/sync for admin or /game-wallets/{id}/sync for user
            const response = await apiService.post<{
                gameWallet?: GameWallet;
                syncStatus?: string;
                lastSyncedAt?: string;
                source?: string;
                failedAt?: string;
                lastError?: string;
                errorSource?: string;
                transferredAt?: string;
            }>(`/game-wallets/${gameWalletId}/sync`, {}, true);

            return {
                success: response.success,
                message: response.message,
                data: response.data,
                statusCode: response.success ? 200 : 400
            };

        } catch (error: any) {
            console.error('Error syncing game wallet:', error);

            // Handle specific error cases
            if (error.statusCode === 401) {
                return {
                    success: false,
                    message: 'Authentication required. Please log in again.',
                    statusCode: 401
                };
            }

            if (error.statusCode === 403) {
                return resolveForbiddenPaymentError(error, 'Insufficient permissions to sync game wallet');
            }

            if (error.statusCode === 404) {
                return {
                    success: false,
                    message: error.message || 'Game wallet not found',
                    statusCode: 404
                };
            }

            return {
                success: false,
                message: error.message || 'Failed to sync game wallet. Please try again.',
                statusCode: error.statusCode || 500
            };
        }
    }

    /**
     * Validates deposit data
     */
    private validateDepositData(depositData: DepositRequest): { isValid: boolean; message: string } {
        if (!depositData.amount || depositData.amount <= 0) {
            return {
                isValid: false,
                message: 'Amount must be greater than 0'
            };
        }

        // Min do backend enforce theo config admin per-provider (multiGameDepositService.js:341).
        // FE only keeps the max cap.

        if (depositData.amount > 1000.00) {
            return {
                isValid: false,
                message: 'The maximum deposit amount is $1,000. Please enter a smaller amount.'
            };
        }

        if (!depositData.paymentMethod || typeof depositData.paymentMethod !== 'string') {
            return {
                isValid: false,
                message: 'Payment method is required'
            };
        }

        return {
            isValid: true,
            message: 'Valid deposit data'
        };
    }

    /**
     * Validates withdrawal data
     */
    private validateWithdrawalData(withdrawalData: WithdrawalRequest): { isValid: boolean; message: string } {
        if (!withdrawalData.amount || withdrawalData.amount <= 0) {
            return {
                isValid: false,
                message: 'Amount must be greater than 0'
            };
        }

        if (withdrawalData.amount < 10.00) {
            return {
                isValid: false,
                message: 'Minimum withdrawal amount is $10.00'
            };
        }

        if (withdrawalData.amount > 500.00) {
            return {
                isValid: false,
                message: 'Maximum withdrawal amount is $500.00'
            };
        }

        const validMethods = ['bank_transfer', 'paypal', 'check'];
        if (!validMethods.includes(withdrawalData.withdrawalMethod)) {
            return {
                isValid: false,
                message: 'Invalid withdrawal method'
            };
        }

        return {
            isValid: true,
            message: 'Valid withdrawal data'
        };
    }

    /**
     * Formats currency amount for display
     */
    formatCurrency(amount: number, currency: string = 'USD'): string {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: currency
        }).format(amount);
    }

    /**
     * Formats transaction status for display
     */
    formatTransactionStatus(status: string): string {
        const statusMap: { [key: string]: string } = {
            'pending': 'Pending',
            'completed': 'Completed',
            'failed': 'Failed',
            'cancelled': 'Cancelled'
        };

        return statusMap[status] || status;
    }

    /**
     * Gets the token text colour class for a transaction status
     */
    getStatusColor(status: string): string {
        const colorMap: { [key: string]: string } = {
            'pending': 'text-warning',
            'completed': 'text-success',
            'failed': 'text-danger',
            'cancelled': 'text-tertiary'
        };

        return colorMap[status] || 'text-tertiary';
    }
}

// Export singleton instance
export const paymentService = new PaymentService();

// Game Wallet Management Types
export interface GameWalletItem {
    game: {
        id: string;
        code: string;
        name: string;
        status: 'active' | 'inactive' | 'maintenance';
        currency: string;
        minDeposit: number;
        maxDeposit: number;
        /** URL trang game (chỉ dùng cho client mobile) */
        gameUrl?: string | null;
        /** Admin "Play inside the app": the game site may run in the app's full-screen player (/play/:gameId). */
        playInFrame?: boolean;
        /** `manual`: staff add and take out money by hand (moves out wait for their approval). */
        depositType?: 'auto' | 'manual';
    };
    account: {
        id: string;
        gameUsername: string;
        password?: string;
        gameAccountId: string;
        status: 'active' | 'inactive';
        lastSyncedAt: string;
        createdAt: string;
        updatedAt: string;
    };
    balance: {
        walletBalance: number;
        /** Null when this game has never had a successful balance read. */
        inGameBalance: number | null;
        /** River Pay sweepstakes withdrawable; omitted or 0 for other games */
        winningBalance?: number;
        syncStatus: 'in_sync' | 'out_of_sync' | 'syncing' | 'error' | 'pending' | 'never_synced' | 'failed';
        lastSyncedAt: string | null;
        /** False when inGameBalance is the previous read, or there is no read yet. */
        balanceLive?: boolean;
        balanceReason?: string | null;
    };
}

export interface GameWalletsResponse {
    items: GameWalletItem[];
}

/**
 * Gets user's game wallets with account and balance information
 * @returns Promise with game wallets data
 */
export async function getUserGameWallets(): Promise<ApiResponse<GameWalletsResponse>> {
    try {
        const response = await apiService.get<GameWalletsResponse>('/game-wallets/games', true);
        return response;
    } catch (error: any) {
        console.error('Error fetching user game wallets:', error);
        throw error;
    }
}

/**
 * GET /game-wallets — user wallets with provider (any status) + balances.
 * Used to recover in-game balances when /games drops inactive providers.
 */
export async function getUserRawGameWallets(): Promise<ApiResponse<{ items: unknown[] }>> {
    try {
        return await apiService.get<{ items: unknown[] }>('/game-wallets', true);
    } catch (error: any) {
        console.error('Error fetching raw game wallets:', error);
        throw error;
    }
}

export interface ProviderFeeRateData {
    feeRate: number;
    systemFeeRate?: number;
    gatewayFeeRate?: number;
    paymentGateFeeMinUsd?: number;
    networkFeeType?: 'none' | 'percent' | 'fixed';
    networkFeeRate?: number;
    networkFeeAmount?: number;
    matchedMethodId?: string | null;
    provider?: string;
    operation?: string;
    methodId?: string;
}

/**
 * Public GET /payment/provider-fee-rate (no auth).
 */
export async function fetchProviderFeeRate(params: {
    provider: string;
    operation: 'withdrawal' | 'deposit' | 'transfer';
    methodId?: string;
}): Promise<{ success: boolean; data?: ProviderFeeRateData; message?: string }> {
    const qs = new URLSearchParams({
        provider: params.provider.trim().toLowerCase(),
        operation: params.operation,
        methodId: (params.methodId || 'all').trim().toLowerCase()
    });
    const response = await apiService.get<ProviderFeeRateData>(`/payment/provider-fee-rate?${qs.toString()}`, false);
    return {
        success: response.success,
        data: response.data,
        message: response.message
    };
}

// Debug log only in development
if (import.meta.env.DEV) {
    console.log('[PaymentService] Instance created:', {
        methods: Object.getOwnPropertyNames(Object.getPrototypeOf(paymentService)).filter(name => name !== 'constructor')
    });
}

// Types are already exported above with their interfaces
// No need to re-export them here
